
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';

export default function InspectionFlow() {
  const navigate = useNavigate();

  const steps = [
    {
      number: '01',
      title: 'ご予約',
      description: 'お電話またはWebフォームからご予約ください。お客様のご都合に合わせて日程を調整いたします。',
      icon: 'ri-calendar-line',
      details: [
        'お電話: 0120-000-000',
        'Web予約: 24時間受付',
        '代車のご用意も可能'
      ]
    },
    {
      number: '02',
      title: 'ご来店・受付',
      description: 'ご予約いただいた日時にご来店ください。受付にて車検証と自賠責保険証をお預かりいたします。',
      icon: 'ri-user-line',
      details: [
        '車検証をご持参ください',
        '自賠責保険証をご持参ください',
        '受付時間: 9:00〜17:00'
      ]
    },
    {
      number: '03',
      title: '点検・お見積もり',
      description: '国家資格を持つ整備士が、法定24ヶ月点検を実施。点検結果をもとに詳細なお見積もりをご提示します。',
      icon: 'ri-search-line',
      details: [
        '法定24ヶ月点検実施',
        'コンピューター診断',
        '詳細見積もり提示'
      ]
    },
    {
      number: '04',
      title: '整備・検査',
      description: 'お見積もりにご了承いただいた後、必要な整備を実施。その後、保安基準適合検査を行います。',
      icon: 'ri-tools-line',
      details: [
        '必要箇所の整備実施',
        '部品交換（必要に応じて）',
        '保安基準適合検査'
      ]
    },
    {
      number: '05',
      title: 'お引き渡し',
      description: '車検完了後、新しい車検証と点検記録簿をお渡しし、整備内容をご説明いたします。',
      icon: 'ri-car-line',
      details: [
        '新車検証発行',
        '点検記録簿お渡し',
        '整備内容のご説明'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <Breadcrumb />
      <main className="pt-8">
        {/* Hero Section */}
        <section 
          className="py-16 bg-gray-50 relative bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/b4de47c672262797f7a8a70612bd044c.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              車検の流れ
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              ご予約から車検完了まで、安心してお任せいただけるよう、<br />
              わかりやすい流れでご案内いたします。
            </p>
          </div>
        </section>

        {/* Steps */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-12">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  {index < steps.length - 1 && (
                    <div className="absolute left-8 top-20 w-0.5 h-24 bg-orange-200 hidden md:block"></div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-2 text-center md:text-left">
                      <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto md:mx-0 mb-4">
                        <span className="text-xl font-bold">{step.number}</span>
                      </div>
                    </div>
                    <div className="md:col-span-6">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                          <i className={`${step.icon} text-2xl text-orange-500`}></i>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {step.description}
                      </p>
                      <ul className="space-y-2">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <i className="ri-check-line text-orange-500 mr-2"></i>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="md:col-span-4">
                      <img 
                        src={`https://readdy.ai/api/search-image?query=${
                          index === 0 ? 'Customer at automotive service reception making appointment friendly staff helping schedule car inspection clean modern office environment professional service' :
                          index === 1 ? 'Customer arriving at car service center reception area staff receiving documents vehicle registration papers welcoming professional environment' :
                          index === 2 ? 'Professional mechanic inspecting vehicle detailed examination diagnostic equipment modern automotive workshop clean organized workspace' :
                          index === 3 ? 'Skilled technician performing car maintenance repair work using proper tools replacing parts professional automotive garage setting' :
                          'Happy customer receiving completed vehicle after service new documents staff explaining work done automotive service center professional handover'
                        }&width=400&height=250&seq=step-${index + 1}-image&orientation=landscape`}
                        alt={step.title}
                        className="w-full h-48 object-cover object-top rounded-lg shadow-md"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-800 text-center mb-12">
              当工場の車検の特徴
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '国土交通省認証工場',
                  description: '法定基準に基づいた確実な車検を実施。安心してお任せください。'
                },
                {
                  icon: 'ri-time-line',
                  title: '最短1日完了',
                  description: '事前予約で最短1日での車検完了が可能です。'
                },
                {
                  icon: 'ri-money-dollar-circle-line',
                  title: '明確な料金体系',
                  description: '事前見積もりで安心。追加料金は必ずご相談いたします。'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${feature.icon} text-3xl text-orange-500`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              車検のご予約はこちら
            </h2>
            <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
              お電話またはWebフォームから、お気軽にご予約ください。<br />
              代車のご用意も承っております。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" onClick={() => navigate('/contact')}>
                Web予約はこちら
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white hover:text-slate-800"
              >
                <i className="ri-phone-line mr-2"></i>
                0120-000-000
              </Button>
            </div>
            <p className="text-gray-300">受付時間: 9:00〜18:00（日曜・祝日除く）</p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
